// src/components/NotificationBell.tsx
import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Drawer,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  NotificationsNoneOutlined,
  NotificationsActive,
  CloseOutlined,
} from '@mui/icons-material';
import { useNotifications, AppNotification } from '../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n/useT';

export const NotificationBell: React.FC = () => {
  const t = useT();
  const { notifications, unseenCount, markSeen, markAllSeen, dismiss } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleTapNotification = (n: AppNotification) => {
    markSeen(n.id);
    setOpen(false);
    // Deep-link into Discover with this item id. Discover page reads
    // the `item` query param and scrolls/opens that card.
    navigate(`/discover?item=${encodeURIComponent(n.feed_item_id)}`);
  };

  return (
    <>
      <IconButton
        aria-label={unseenCount > 0 ? `${unseenCount} new notifications` : 'Notifications'}
        onClick={handleOpen}
        size="medium"
      >
        <Badge
          badgeContent={unseenCount > 9 ? '9+' : unseenCount}
          color="error"
          invisible={unseenCount === 0}
        >
          {unseenCount > 0 ? (
            <NotificationsActive />
          ) : (
            <NotificationsNoneOutlined />
          )}
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        ModalProps={{ keepMounted: false }}
      >
        <Box sx={{ width: 340, maxWidth: '90vw', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              
              {t("Notifications")}
            </Typography>
            <IconButton size="small" onClick={handleClose} aria-label={t("Close")}>
              <CloseOutlined />
            </IconButton>
          </Box>

          {unseenCount > 0 && (
            <Button
              variant="text"
              size="small"
              onClick={markAllSeen}
              sx={{ alignSelf: 'flex-start', ml: 1, mt: 0.5 }}
            >
              
              {t("Mark all read")}
            </Button>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 40, mb: 1 }}>🔕</Typography>
                <Typography variant="body2" color="text.secondary">
                  
                  {t("Nothing new right now. Notifications arrive when there's something genuinely worth your attention.")}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {notifications.map((n, i) => (
                  <React.Fragment key={n.id}>
                    <ListItemButton
                      onClick={() => handleTapNotification(n)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        bgcolor: n.seen ? 'transparent' : 'action.hover',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: n.seen ? 'transparent' : 'primary.main',
                          mr: 1.5,
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: n.seen ? 500 : 700,
                                lineHeight: 1.3,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {n.title}
                            </Typography>
                            {n.merged_item_ids && n.merged_item_ids.length > 1 && (
                              <Box
                                sx={{
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  bgcolor: n.seen
                                    ? 'action.hover'
                                    : 'primary.main',
                                  color: n.seen
                                    ? 'text.secondary'
                                    : 'primary.contrastText',
                                  px: 0.75,
                                  py: 0.125,
                                  borderRadius: 1,
                                  letterSpacing: 0.5,
                                  textTransform: 'uppercase',
                                  flexShrink: 0,
                                }}
                              >
                                {n.merged_item_ids.length}  {t("picks")}
                              </Box>
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {n.merged_item_titles &&
                              n.merged_item_titles.length > 1 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    mt: 0.25,
                                    lineHeight: 1.4,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {n.merged_item_titles
                                    .slice(0, 4)
                                    .join(' · ')}
                                  {n.merged_item_titles.length > 4 &&
                                    ` · +${n.merged_item_titles.length - 4} more`}
                                </Typography>
                              )}
                            {n.rationale &&
                              !(
                                n.merged_item_titles &&
                                n.merged_item_titles.length > 1
                              ) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
                                >
                                  {n.rationale}
                                </Typography>
                              )}
                            {n.created_at && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                {formatTime(n.created_at)}
                              </Typography>
                            )}
                          </>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <IconButton
                        size="small"
                        aria-label={t("Dismiss")}
                        onClick={(e) => {
                          e.stopPropagation();
                          dismiss(n.id);
                        }}
                        sx={{ ml: 0.5 }}
                      >
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                    {i < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

function formatTime(d: Date): string {
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return d.toLocaleDateString();
}
